import {
  GetString,
} from 'fluent-react';
import {
  parseToRgb,
} from 'polished';
import React, { useState } from 'react';
import styled from 'styled-components';
import { formatTradeValueUSD } from '../../numberFormatters';
import {
  labelFontSize,
} from '../exportGrowthDynamicsChart/Chart';
import {
  minNodeRadius,
} from '../exportGrowthDynamicsChart/nodeFillShadersUtils';
import {
  getRadiusScale,
} from '../exportGrowthDynamicsChart/transform';
import { primaryColor1 } from '../Utils';

//#region Styling
const maxNodeRadius = 27;
const strokeColorAsTriplet = parseToRgb(primaryColor1);
const CircleElement = styled.div`
  width: var(--circle-size);
  height: var(--circle-size);
  border-radius: 50%;
  border:
    1px solid rgba(
      ${strokeColorAsTriplet.red},
      ${strokeColorAsTriplet.green},
      ${strokeColorAsTriplet.blue},
      0.5
    );
  position: absolute;
  bottom: 0;
  left: ${maxNodeRadius}px;
  transform: translateX(-50%);
`;
const tooltipStrokeColor = primaryColor1;
const tooltipFillColor = 'white';
const tooltipTriangleHalfBase = 5;
const tooltipTriangleHeight = 5;
const tooltipTriangleBorderWidth = 1;
const Tooltip = styled.div`
  position: absolute;
  padding: 10px;
  background-color: ${tooltipFillColor};
  border: 1px solid ${tooltipStrokeColor};
  transform: translateX(-50%) translateY(${- tooltipTriangleBorderWidth - tooltipTriangleHeight}px);
  color: ${primaryColor1};
  font-size: 0.75rem;
  left: ${maxNodeRadius}px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-left-style: solid;
    border-right-style: solid;
    border-left-color: transparent;
    border-right-color: transparent;
    border-top-style: solid;
  }

  &::before {
    border-left-width: ${tooltipTriangleHalfBase + tooltipTriangleBorderWidth}px;
    border-right-width: ${tooltipTriangleHalfBase + tooltipTriangleBorderWidth}px;
    border-top-width: ${tooltipTriangleHeight + tooltipTriangleBorderWidth}px;
    border-top-color: ${tooltipStrokeColor};
  }

  &::after {
    border-left-width: ${tooltipTriangleHalfBase}px;
    border-right-width: ${tooltipTriangleHalfBase}px;
    border-top-width: ${tooltipTriangleHeight}px;
    border-top-color: ${tooltipFillColor};
  }
`;
const extremumLabelDistanceFromLeft = 80; // in pixels
const extremumLabelSeparationFromlLine = 5;
const ExtremumLabel = styled.div`
  position: absolute;
  left: ${extremumLabelDistanceFromLeft}px;
  color: ${primaryColor1};
  font-size: ${labelFontSize};
`;
const ExtremumConnectingLine = styled.div`
  position: absolute;
  height: 1px;
  background-color: ${primaryColor1};
`;
//#endregion

interface CircleProps {
  index: number;
  size: number;
  onMouseEnter: (index: number) => void;
  onMouseLeave: () => void;
  hoveredIndex: number | undefined;
}
const Circle = ({index, size, onMouseEnter, onMouseLeave, hoveredIndex}: CircleProps) => {
  const style: React.CSSProperties = {
    // Highlight circles smaller than the hovered one:
    'backgroundColor':  (hoveredIndex !== undefined && hoveredIndex <= index) ?
                        '#c6ccdf' : '#dde3ef',
    '--circle-size': `${size}px`,
  };
  return (
    <CircleElement
      style={style}
      onMouseEnter={() => onMouseEnter(index)}
      onMouseLeave={() => onMouseLeave()}
    />
  );
};

interface Props {
  max: number;
  min: number;
  getFluentString: GetString;

  __forceHover?: number;
}

export default (props: Props) => {
  const [hovered, setHovered] = useState<number | undefined>(undefined);
  const {max, min, getFluentString} = props;
  const scale = getRadiusScale([min, max], [minNodeRadius * 2, maxNodeRadius * 2]);

  // We try several calls to `scale.ticsk` to ensure that we have at least 2
  // circles in the legend between min and max:
  let intermediateTicks: number[];
  const twoTicksRequested = scale.ticks(2);
  if (twoTicksRequested.length >= 2) {
    intermediateTicks = twoTicksRequested;
  } else {
    const threeTicksRequested = scale.ticks(3);
    if (threeTicksRequested.length >= 2) {
      intermediateTicks = threeTicksRequested;
    } else {
      const fourTicksRequested = scale.ticks(4);
      intermediateTicks = fourTicksRequested;
    }
  }
  // reverse so that smaller circles appear in front of larger circles
  const allTicks = [min, ...intermediateTicks, max].reverse();

  let actualHovered: typeof hovered;
  if (process.env.NODE_ENV === 'production') {
    actualHovered = hovered;
  } else {
    actualHovered = (props.__forceHover === undefined) ? hovered : props.__forceHover;
  }

  const onMouseEnter = (index: number) => {
    setHovered(index);
  };
  const onMouseLeave = () => {
    setHovered(undefined);
  };
  const circles = allTicks.map((value, index) => {
    const size = scale(value);
    return (
      <Circle
        index={index}
        size={size}
        key={`circle-${value}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        hoveredIndex={actualHovered}
      />
    );
  });

  let hoverTooltip: React.ReactElement<any> | null;
  if (actualHovered === undefined) {
    hoverTooltip = null;
  } else {
    const hoveredTickValue = allTicks[actualHovered];
    if (hoveredTickValue === undefined) {
      hoverTooltip = null;
    } else {
      const tooltipStyle: React.CSSProperties = {
        bottom: `${scale(hoveredTickValue)}px`,
      };
      hoverTooltip = (
        <Tooltip style={tooltipStyle}> {formatTradeValueUSD(hoveredTickValue, getFluentString)} </Tooltip>
      );
    }
  }

  const maxLabelStyle: React.CSSProperties = {
    bottom: `${maxNodeRadius}px`,
    transform: 'translateY(50%)',
  };
  const maxLabel = (
    <ExtremumLabel style={maxLabelStyle}>{formatTradeValueUSD(max, getFluentString)}</ExtremumLabel>
  );
  const minLabelStyle: React.CSSProperties = {
    bottom: `${minNodeRadius}px`,
    transform: 'translateY(50%)',
  };
  const minLabel = (
    <ExtremumLabel style={minLabelStyle}>{formatTradeValueUSD(min, getFluentString)}</ExtremumLabel>
  );

  const maxConnectingLineStyle: React.CSSProperties = {
    left: `${maxNodeRadius * 2}px`,
    width: `${extremumLabelDistanceFromLeft - extremumLabelSeparationFromlLine - maxNodeRadius * 2}px`,
    bottom: `${maxNodeRadius}px`,
  };
  const maxConnectingLine = (
    <ExtremumConnectingLine style={maxConnectingLineStyle}/>
  );

  const minConnectingLineStyle: React.CSSProperties = {
    left: `${maxNodeRadius + minNodeRadius}px`,
    width: `${extremumLabelDistanceFromLeft - extremumLabelSeparationFromlLine - maxNodeRadius - minNodeRadius}px`,
    bottom: `${minNodeRadius}px`,
  };
  const minConnectingLine = (
    <ExtremumConnectingLine style={minConnectingLineStyle}/>
  );

  return (
    <>
      {circles}
      {maxConnectingLine}
      {minConnectingLine}
      {maxLabel}
      {minLabel}
      {hoverTooltip}
    </>
  );
};
